import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, FlaskConical, Scan, HeartPulse, BrainCircuit, Wind, Heart, Brain, Activity, Microscope, Stethoscope } from 'lucide-react';
import { publicAPI } from '../services/api';
import { Button } from './ui/button';

const iconMap = {
  FlaskConical: FlaskConical,
  Scan: Scan,
  HeartPulse: HeartPulse,
  BrainCircuit: BrainCircuit,
  Wind: Wind,
  Heart: Heart,
  Brain: Brain,
  Activity: Activity,
  Microscope: Microscope,
  Stethoscope: Stethoscope,
  // Map service names to appropriate icons
  Cardiology: Heart,
  Electrophysiology: Brain
};

// Animated Service Icon Component
const AnimatedServiceIcon = ({ icon: IconName, serviceName = "", className = "" }) => {
  // Check if there's a specific icon for this service name
  const Icon = iconMap[serviceName] || iconMap[IconName] || FlaskConical;
  
  return (
    <div className={`relative ${className}`}>
      {/* Pulsing background ring */}
      <div className="absolute inset-0 rounded-xl bg-[#007897]/20 animate-ping opacity-20" style={{ animationDuration: '2s' }} />
      {/* Rotating gradient border */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#007897] via-[#00a0af] to-[#3949ab] opacity-30 animate-spin" style={{ animationDuration: '8s' }} />
      {/* Icon container */}
      <div className="relative w-full h-full bg-gradient-to-br from-[#007897]/10 to-[#3949ab]/10 rounded-xl flex items-center justify-center group-hover:from-[#007897] group-hover:to-[#3949ab] transition-all duration-300">
        <Icon className="w-7 h-7 text-[#007897] group-hover:text-white transition-colors animate-pulse" style={{ animationDuration: '2s' }} />
      </div>
    </div>
  );
};

// Service categories/headers
const serviceCategories = [
  { id: 'all', name: 'All Services', icon: null },
  { id: 'Pathology', name: 'Pathology', icon: FlaskConical },
  { id: 'Radiology', name: 'Radiology', icon: Scan },
  { id: 'Cardiology', name: 'Cardiology', icon: Heart },
  { id: 'Neurology', name: 'Neurology', icon: BrainCircuit },
  { id: 'Respiratory', name: 'Respiratory', icon: Wind }
];

const ServicesSection = () => {
  const scrollRef = useRef(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await publicAPI.getServices();
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -350 : 350;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
          <div>
            <span className="text-[#007897] font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Quality in Every Service
            </h2>
            <p className="text-gray-600 max-w-xl">
              Explore our comprehensive range of diagnostic services designed to meet all your healthcare needs.
            </p>
          </div>
          <div className="flex gap-2 mt-6 md:mt-0">
            <button
              onClick={() => scroll('left')}
              className="p-3 rounded-full bg-white border border-gray-200 hover:border-[#007897] hover:text-[#007897] transition-colors shadow-sm"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-3 rounded-full bg-white border border-gray-200 hover:border-[#007897] hover:text-[#007897] transition-colors shadow-sm"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Category Header Buttons */}
        <div className="flex flex-wrap gap-2 mb-8">
          {serviceCategories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-[#007897] text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-[#007897] hover:text-[#007897]'
                }`}
              >
                {Icon && <Icon size={16} />}
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Services Carousel */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filteredServices.map((service) => {
              const Icon = iconMap[service.icon] || FlaskConical;
              // Get sub-services count
              const subServices = service.sub_services || service.subServices || [];
              
              return (
                <div
                  key={service.id}
                  className="group flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 snap-start border border-gray-100 hover:border-[#007897]/20"
                >
                  <AnimatedServiceIcon icon={service.icon} serviceName={service.name} className="w-14 h-14 mb-5" />
                  
                  <span className="text-xs font-medium text-[#5c6bc0] uppercase tracking-wider">
                    {service.category}
                  </span>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-3 group-hover:text-[#007897] transition-colors">
                    {service.name}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {service.description}
                  </p>
                  
                  {/* Categories count display */}
                  <div className="mb-5">
                    <span className="text-sm text-gray-500">
                      {subServices.length > 0 ? `${subServices.length} categories` : 'Specialized diagnostics'}
                    </span>
                  </div>
                  
                  <Link
                    to={`/services/${service.slug}`}
                    className="inline-flex items-center text-[#007897] font-medium text-sm hover:gap-3 transition-all"
                  >
                    View All Tests
                    <ArrowRight size={16} className="ml-2" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/services">
            <Button className="bg-[#007897] hover:bg-[#283593] text-white px-8">
              View All Services
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
